// HR-004 follow-up — Department Floating Role norms admin.
//
// Configure per-department floating role requirements. Example:
//   Ring Frame  · GAITER     · 3 ops
//   Ring Frame  · SWEEPER    · 1 op
//   Preparatory · SWEEPER    · 1 op
//   Preparatory · RELIEVER   · 1 op
//   Link Coner  · SWEEPER    · 1 op
//   Link Coner  · RELIEVER   · 1 op
//
// Embedded on the Sanctioned Strength page, right below Machine Groups.

import { useMemo, useState } from 'react'
import {
  App, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Popconfirm,
  Select, Space, Switch, Table, Tag, Typography,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  deptRoleNormsApi, workerRolesApi,
  type DeptRoleNorm, type DeptRoleNormCreate, type DeptRoleNormPatch,
} from '@/features/hr/manpowerApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography


export default function DepartmentFloatingRolesCard() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<DeptRoleNorm | null>(null)

  const { data: depts } = useQuery({
    queryKey: ['drn-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200, plant_id: plantId }),
    enabled: !!plantId,
  })
  const { data: workerRoles } = useQuery({
    queryKey: ['drn-worker-roles', plantId],
    queryFn: () => workerRolesApi.list({
      plant_id: plantId ?? undefined, is_active: true,
    }),
    enabled: !!plantId,
  })
  const { data: norms, isLoading } = useQuery({
    queryKey: ['dept-role-norms', plantId],
    queryFn: () => deptRoleNormsApi.list({ plant_id: plantId! }),
    enabled: !!plantId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['dept-role-norms'] })

  const createMut = useMutation({
    mutationFn: (body: DeptRoleNormCreate) => deptRoleNormsApi.create(body),
    onSuccess: () => { message.success('Requirement added'); invalidate(); setCreateOpen(false) },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: DeptRoleNormPatch }) =>
      deptRoleNormsApi.patch(id, body),
    onSuccess: () => { message.success('Updated'); invalidate(); setEditing(null) },
  })

  const removeMut = useMutation({
    mutationFn: (id: string) => deptRoleNormsApi.remove(id),
    onSuccess: () => { message.success('Deactivated'); invalidate() },
  })

  const deptName = (id: string) => depts?.items?.find((d) => d.id === id)?.name ?? '—'
  const roleColor = (code: string) =>
    workerRoles?.find((r) => r.role_code === code)?.color_tag ?? 'default'
  const roleName = (code: string) =>
    workerRoles?.find((r) => r.role_code === code)?.role_name ?? code

  const columns = [
    {
      title: 'Department', key: 'dept',
      render: (_: unknown, r: DeptRoleNorm) => deptName(r.department_id),
    },
    {
      title: 'Role', dataIndex: 'role_code', key: 'role',
      render: (s: string) => (
        <Space size={4}>
          <Tag color={roleColor(s)} style={{ margin: 0, fontWeight: 700 }}>{s}</Tag>
          {roleName(s) !== s && (
            <Text type="secondary" style={{ fontSize: 11 }}>{roleName(s)}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Required', dataIndex: 'required_count', key: 'req',
      render: (n: number) => (
        <Text strong style={{ fontSize: 14 }}>{n} op{n === 1 ? '' : 's'}</Text>
      ),
    },
    {
      title: 'Skill ≥', dataIndex: 'required_skill_level', key: 'sk',
      render: (n: number) => <Text type="secondary">{n}</Text>,
    },
    {
      title: 'Flags', key: 'flags',
      render: (_: unknown, r: DeptRoleNorm) => (
        <Space size={2}>
          {r.critical_post_flag && <Tag color="red" style={{ margin: 0 }}>CRIT</Tag>}
          {r.certification_required && <Tag color="purple" style={{ margin: 0 }}>CERT</Tag>}
        </Space>
      ),
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'st',
      render: (b: boolean) => (
        <Tag color={b ? 'success' : 'default'}>{b ? 'ACTIVE' : 'INACTIVE'}</Tag>
      ),
    },
    ...(canEdit ? [{
      title: '', key: 'actions',
      render: (_: unknown, r: DeptRoleNorm) => (
        <Space size={2}>
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => setEditing(r)} />
          <Popconfirm
            title="Deactivate this requirement?"
            onConfirm={() => removeMut.mutate(r.id)}
            okText="Deactivate" okButtonProps={{ danger: true }}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ]

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <Space>
          <span>Department Floating Roles</span>
          <Tag color="purple">Roving roles per dept per shift</Tag>
        </Space>
      }
      extra={canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          New Requirement
        </Button>
      )}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Configure floating roles each department needs per shift — Gaiter, Jobber,
        Sweeper, Reliever, etc. These are roving roles (not pinned to a machine).
        Worker Allocation board shows them as expected seats so nothing is missed.
      </Text>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns as never}
        dataSource={norms ?? []}
        pagination={false}
        size="small"
      />

      <CreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        depts={depts?.items ?? []}
        workerRoles={workerRoles ?? []}
        onSubmit={(body) => createMut.mutate(body)}
        submitting={createMut.isPending}
      />
      <EditModal
        norm={editing}
        onClose={() => setEditing(null)}
        onSubmit={(id, body) => patchMut.mutate({ id, body })}
        submitting={patchMut.isPending}
      />
    </Card>
  )
}


// ─── Modals ─────────────────────────────────────────────────────────────

function CreateModal({ open, onClose, depts, workerRoles, onSubmit, submitting }: {
  open: boolean
  onClose: () => void
  depts: Array<{ id: string; name: string }>
  workerRoles: Array<{ role_code: string; role_name: string; role_type: string; color_tag: string }>
  onSubmit: (body: DeptRoleNormCreate) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const { plantId } = usePlantId()

  // Show only FLOATING / BOTH roles (machine-bound roles don't make sense per-dept)
  const eligibleRoles = useMemo(() => workerRoles.filter((r) =>
    r.role_type === 'FLOATING' || r.role_type === 'BOTH'
  ), [workerRoles])

  return (
    <Modal
      open={open} onCancel={onClose}
      title="New Department Floating Role Requirement" width={520}
      okText="Add" confirmLoading={submitting}
      onOk={() => {
        form.validateFields().then((v) => {
          if (!plantId) return
          onSubmit({
            plant_id: plantId,
            department_id: v.department_id,
            role_code: v.role_code,
            required_count: v.required_count ?? 1,
            required_skill_level: v.required_skill_level ?? 0,
            critical_post_flag: !!v.critical_post_flag,
            certification_required: !!v.certification_required,
            effective_from: v.effective_from?.format('YYYY-MM-DD'),
            effective_to: v.effective_to?.format('YYYY-MM-DD') ?? null,
            remarks: v.remarks || null,
          })
        })
      }}
    >
      <Form form={form} layout="vertical" initialValues={{
        required_count: 1,
        required_skill_level: 0,
        effective_from: dayjs(),
      }}>
        <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
          <Select options={depts.map((d) => ({ label: d.name, value: d.id }))} />
        </Form.Item>
        <Form.Item name="role_code" label="Role" rules={[{ required: true }]}>
          <Select
            showSearch optionFilterProp="label"
            options={eligibleRoles.map((r) => ({
              label: (
                <Space size={6}>
                  <Tag color={r.color_tag} style={{ margin: 0, fontSize: 10 }}>{r.role_code}</Tag>
                  <span>{r.role_name !== r.role_code ? r.role_name : ''}</span>
                </Space>
              ),
              value: r.role_code,
            }))}
          />
        </Form.Item>
        <Form.Item name="required_count" label="Required count per shift"
          tooltip="How many of this role per shift. E.g. 3 GAITERS for Ring Frame.">
          <InputNumber min={1} max={50} style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="required_skill_level" label="Minimum skill level (0-5)">
          <InputNumber min={0} max={5} style={{ width: 200 }} />
        </Form.Item>
        <Space>
          <Form.Item name="critical_post_flag" label="Critical Post"
            valuePropName="checked" style={{ marginBottom: 0 }}>
            <Switch />
          </Form.Item>
          <Form.Item name="certification_required" label="Certification Required"
            valuePropName="checked" style={{ marginBottom: 0 }}>
            <Switch />
          </Form.Item>
        </Space>
        <Form.Item name="effective_from" label="Effective From" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="effective_to" label="Effective Till (optional)">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}


function EditModal({ norm, onClose, onSubmit, submitting }: {
  norm: DeptRoleNorm | null
  onClose: () => void
  onSubmit: (id: string, body: DeptRoleNormPatch) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  return (
    <Modal
      open={!!norm} onCancel={onClose}
      title={norm ? `Edit ${norm.role_code}` : 'Edit'}
      width={520} okText="Save" confirmLoading={submitting}
      onOk={() => {
        if (!norm) return
        form.validateFields().then((v) => {
          onSubmit(norm.id, {
            required_count: v.required_count,
            required_skill_level: v.required_skill_level,
            critical_post_flag: !!v.critical_post_flag,
            certification_required: !!v.certification_required,
            effective_to: v.effective_to?.format('YYYY-MM-DD') ?? null,
            remarks: v.remarks || null,
          })
        })
      }}
      destroyOnClose
    >
      {norm && (
        <Form form={form} layout="vertical" initialValues={{
          required_count: norm.required_count,
          required_skill_level: norm.required_skill_level,
          critical_post_flag: norm.critical_post_flag,
          certification_required: norm.certification_required,
          effective_to: norm.effective_to ? dayjs(norm.effective_to) : null,
          remarks: norm.remarks ?? '',
        }}>
          <Form.Item name="required_count" label="Required count">
            <InputNumber min={1} max={50} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="required_skill_level" label="Minimum skill level">
            <InputNumber min={0} max={5} style={{ width: 200 }} />
          </Form.Item>
          <Space>
            <Form.Item name="critical_post_flag" label="Critical Post"
              valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
            <Form.Item name="certification_required" label="Certification Required"
              valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="effective_to" label="Effective Till">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
